import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, Paper, Grid, Chip, Skeleton, IconButton, Tooltip } from '@mui/material';
import { PlayArrow, EmojiEvents } from '@mui/icons-material';
import { API_ENDPOINTS } from '../../../utils/config';
import { THEME } from '../../../theme';
import { buildGameNameMap, gameDisplayName, formatAgentLabel } from './displayHelpers';
import { EmptyState, ErrorState } from '../../../Components/Arcade/ui/StateViews';

function HighlightCard({ item, gameNameMap }) {
    const runId = item.run_id || item._id || item.id;
    const gameId = item.game_id || item.game;
    return (
        <Paper sx={{
            p: 2.5,
            bgcolor: 'rgba(255,255,255,0.02)',
            border: `1px solid ${THEME.border}`,
            borderRadius: 3,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmojiEvents sx={{ color: '#fbbf24' }} fontSize="small" />
                <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700 }}>
                    {gameDisplayName(gameId, gameNameMap)}
                </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: THEME.secondary }}>
                {formatAgentLabel(item)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={`Score: ${item.score ?? 0}`} sx={{ bgcolor: THEME.primary, color: 'black', fontWeight: 'bold' }} />
                {item.timestamp && (
                    <Typography variant="caption" sx={{ color: '#666' }}>
                        {new Date(item.timestamp).toLocaleDateString()}
                    </Typography>
                )}
            </Box>
            {runId && (
                <Tooltip title="Play">
                    <IconButton
                        component={Link}
                        to={`/arcade/replay/${runId}`}
                        size="small"
                        aria-label="Play"
                        sx={{ mt: 'auto', color: THEME.primary, alignSelf: 'flex-start' }}
                    >
                        <PlayArrow fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
        </Paper>
    );
}

const HighlightsCarousel = () => {
    const [highlights, setHighlights] = useState([]);
    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const gameNameMap = useMemo(() => buildGameNameMap(games), [games]);

    useEffect(() => {
        let mounted = true;
        async function fetchHighlights() {
            setLoading(true);
            setError(null);
            try {
                const [res, gamesRes] = await Promise.all([
                    fetch(`${API_ENDPOINTS.HIGHLIGHTS}?limit=12`),
                    fetch(API_ENDPOINTS.GAMES_LIST),
                ]);
                if (!res.ok) throw new Error(`status ${res.status}`);
                const data = await res.json();
                const list = data.highlights || (Array.isArray(data) ? data : []);
                if (mounted) setHighlights(list);
                if (gamesRes.ok) {
                    const g = await gamesRes.json();
                    if (mounted) setGames(Array.isArray(g) ? g : (g.games || []));
                }
            } catch (e) {
                if (mounted) setError('Failed to load highlights');
                if (mounted) setHighlights([]);
            }
            if (mounted) setLoading(false);
        }
        fetchHighlights();
        return () => { mounted = false; };
    }, []);

    if (loading) {
        return (
            <Grid container spacing={2}>
                {[...Array(6)].map((_, i) => (
                    <Grid item xs={12} sm={6} md={4} key={i}>
                        <Skeleton variant="rectangular" height={150} sx={{ bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2 }} />
                    </Grid>
                ))}
            </Grid>
        );
    }

    if (error) {
        return <ErrorState message="Highlights can't be shown right now. Please try again in a moment." />;
    }

    if (!highlights.length) {
        return <EmptyState message="No highlights yet — check back after more agents have run." />;
    }

    return (
        <Grid container spacing={2}>
            {highlights.map((item, idx) => (
                <Grid item xs={12} sm={6} md={4} key={item.run_id || item._id || idx}>
                    <HighlightCard item={item} gameNameMap={gameNameMap} />
                </Grid>
            ))}
        </Grid>
    );
};

export default HighlightsCarousel;
